import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import AdminLayout from '@/components/layouts/AdminLayout';
import { adminApi } from '@/services/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  Loader2,
  ChevronRight,
  Building2,
  ExternalLink,
} from 'lucide-react';

const SECTORS = [
  { value: 'technology', label: 'Technology' },
  { value: 'financial_services', label: 'Financial Services' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'retail', label: 'Retail' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'professional_services', label: 'Professional Services' },
  { value: 'media_entertainment', label: 'Media & Entertainment' },
  { value: 'energy', label: 'Energy' },
  { value: 'other', label: 'Other' },
];

const HEADCOUNT_BANDS = [
  { value: '100-499', label: '100-499' },
  { value: '500-1499', label: '500-1,499' },
  { value: '1500-4999', label: '1,500-4,999' },
  { value: '5000+', label: '5,000+' },
];

const GEOGRAPHIES = [
  { value: 'GB', label: 'United Kingdom' },
  { value: 'US', label: 'United States' },
  { value: 'AE', label: 'United Arab Emirates' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
  { value: 'SG', label: 'Singapore' },
];

const AdminOrgList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    sector: '',
    prepop_status: '',
    chro_status: '',
  });
  
  // New org modal
  const [showNewOrgModal, setShowNewOrgModal] = useState(searchParams.get('new') === 'true');
  const [newOrgData, setNewOrgData] = useState({
    name: '',
    domain: '',
    sector: '',
    geography_primary: '',
    headcount_band: '',
    linkedin_url: '',
    chro_name: '',
    chro_email: '',
    currency: 'GBP',
    internal_notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchOrgs = async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: 25,
        ...(searchTerm && { search: searchTerm }),
        ...(filters.sector && { sector: filters.sector }),
        ...(filters.prepop_status && { prepop_status: filters.prepop_status }),
        ...(filters.chro_status && { chro_status: filters.chro_status }),
      };
      
      const response = await adminApi.listOrgs(params);
      setOrgs(response.data.items);
      setPagination({
        page: response.data.page,
        pages: response.data.pages,
        total: response.data.total,
      });
    } catch (error) {
      toast.error('Failed to load organisations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrgs();
  }, [filters]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchOrgs(1);
  };

  const handleCreateOrg = async (e) => {
    e.preventDefault();
    
    if (!newOrgData.name || !newOrgData.domain || !newOrgData.sector || 
        !newOrgData.geography_primary || !newOrgData.headcount_band) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      await adminApi.createOrg(newOrgData);
      toast.success('Organisation created successfully');
      setShowNewOrgModal(false);
      setNewOrgData({
        name: '',
        domain: '',
        sector: '',
        geography_primary: '',
        headcount_band: '',
        linkedin_url: '',
        chro_name: '',
        chro_email: '',
        currency: 'GBP',
        internal_notes: '',
      });
      searchParams.delete('new');
      setSearchParams(searchParams);
      fetchOrgs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create organisation');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status, type) => {
    const variants = {
      prepop: {
        complete: { variant: 'default', className: 'bg-accent/20 text-accent', label: 'Complete' },
        in_progress: { variant: 'default', className: 'bg-warning/20 text-warning', label: 'In Progress' },
        failed: { variant: 'destructive', className: '', label: 'Failed' },
        partial: { variant: 'default', className: 'bg-warning/20 text-warning', label: 'Partial' },
        not_started: { variant: 'outline', className: '', label: 'Not Started' },
      },
      chro: {
        active: { variant: 'default', className: 'bg-accent/20 text-accent', label: 'Active' },
        invited: { variant: 'default', className: 'bg-info/20 text-info', label: 'Invited' },
        not_invited: { variant: 'outline', className: '', label: 'Not Invited' },
      },
    };
    
    const config = variants[type]?.[status] || { variant: 'outline', label: status };
    return (
      <Badge variant={config.variant} className={config.className}>
        {config.label}
      </Badge>
    );
  };

  return (
    <AdminLayout 
      title="Organisations" 
      breadcrumbs={[{ label: 'Organisations' }]}
    >
      <div className="space-y-6">
        {/* Search and Filters */}
        <Card className="glass-light">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <form onSubmit={handleSearch} className="flex-1 flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search organisations..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                    data-testid="org-search-input"
                  />
                </div>
                <Button type="submit" variant="secondary">Search</Button>
              </form>
              
              <div className="flex gap-2">
                <Select
                  value={filters.sector || "all"}
                  onValueChange={(value) => setFilters({ ...filters, sector: value === "all" ? "" : value })}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Sector" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sectors</SelectItem>
                    {SECTORS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select
                  value={filters.prepop_status || "all"}
                  onValueChange={(value) => setFilters({ ...filters, prepop_status: value === "all" ? "" : value })}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Pre-pop Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="complete">Complete</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="not_started">Not Started</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                  </SelectContent>
                </Select>
                
                <Button onClick={() => setShowNewOrgModal(true)} data-testid="create-org-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  New Org
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Organisations Table */}
        <Card className="glass-light">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : orgs.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64">
                <Building2 className="h-12 w-12 text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">No organisations found</p>
                <Button 
                  variant="link" 
                  className="mt-2"
                  onClick={() => setShowNewOrgModal(true)}
                >
                  Create your first organisation
                </Button>
              </div>
            ) : (
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Organisation</TableHead>
                    <TableHead>Sector</TableHead>
                    <TableHead>Geography</TableHead>
                    <TableHead>Headcount</TableHead>
                    <TableHead>Pre-pop Status</TableHead>
                    <TableHead>CHRO Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orgs.map((org) => (
                    <TableRow key={org.id} className="hover:bg-muted/30">
                      <TableCell>
                        <Link 
                          to={`/admin/orgs/${org.id}`}
                          className="font-medium hover:text-primary transition-colors"
                          data-testid={`org-row-${org.id}`}
                        >
                          {org.name}
                        </Link>
                        <p className="text-xs text-muted-foreground">{org.domain}</p>
                      </TableCell>
                      <TableCell className="text-sm">
                        {SECTORS.find(s => s.value === org.sector)?.label || org.sector}
                      </TableCell>
                      <TableCell className="text-sm">
                        {GEOGRAPHIES.find(g => g.value === org.geography_primary)?.label || org.geography_primary}
                      </TableCell>
                      <TableCell className="text-sm">{org.headcount_band}</TableCell>
                      <TableCell>{getStatusBadge(org.prepop_status, 'prepop')}</TableCell>
                      <TableCell>{getStatusBadge(org.chro_status, 'chro')}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" asChild>
                          <Link to={`/admin/orgs/${org.id}`}>
                            <ChevronRight className="h-4 w-4" />
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Showing {orgs.length} of {pagination.total} organisations
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page === 1}
                onClick={() => fetchOrgs(pagination.page - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page === pagination.pages}
                onClick={() => fetchOrgs(pagination.page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* New Organisation Modal */}
      <Dialog open={showNewOrgModal} onOpenChange={setShowNewOrgModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Organisation</DialogTitle>
            <DialogDescription>
              Enter the organisation details. Pipeline will need to be triggered separately.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateOrg} className="space-y-6 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="org-name">Organisation Name *</Label>
                <Input
                  id="org-name"
                  value={newOrgData.name}
                  onChange={(e) => setNewOrgData({ ...newOrgData, name: e.target.value })}
                  placeholder="Acme Corp"
                  required
                  data-testid="new-org-name"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="org-domain">Primary Domain *</Label>
                <Input
                  id="org-domain"
                  value={newOrgData.domain}
                  onChange={(e) => setNewOrgData({ ...newOrgData, domain: e.target.value })}
                  placeholder="acme.com"
                  required
                  data-testid="new-org-domain"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="org-sector">Sector *</Label>
                <Select
                  value={newOrgData.sector}
                  onValueChange={(value) => setNewOrgData({ ...newOrgData, sector: value })}
                >
                  <SelectTrigger id="org-sector" data-testid="new-org-sector">
                    <SelectValue placeholder="Select sector" />
                  </SelectTrigger>
                  <SelectContent>
                    {SECTORS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="org-geo">Primary Geography *</Label>
                <Select
                  value={newOrgData.geography_primary}
                  onValueChange={(value) => setNewOrgData({ ...newOrgData, geography_primary: value })}
                >
                  <SelectTrigger id="org-geo" data-testid="new-org-geography">
                    <SelectValue placeholder="Select geography" />
                  </SelectTrigger>
                  <SelectContent>
                    {GEOGRAPHIES.map((g) => (
                      <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="org-headcount">Headcount Band *</Label>
                <Select
                  value={newOrgData.headcount_band}
                  onValueChange={(value) => setNewOrgData({ ...newOrgData, headcount_band: value })}
                >
                  <SelectTrigger id="org-headcount" data-testid="new-org-headcount">
                    <SelectValue placeholder="Select headcount" />
                  </SelectTrigger>
                  <SelectContent>
                    {HEADCOUNT_BANDS.map((h) => (
                      <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="org-currency">Currency</Label>
                <Select
                  value={newOrgData.currency}
                  onValueChange={(value) => setNewOrgData({ ...newOrgData, currency: value })}
                >
                  <SelectTrigger id="org-currency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GBP">GBP (£)</SelectItem>
                    <SelectItem value="USD">USD ($)</SelectItem>
                    <SelectItem value="AED">AED</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2 col-span-2">
                <Label htmlFor="org-linkedin">LinkedIn Company URL</Label>
                <Input
                  id="org-linkedin"
                  value={newOrgData.linkedin_url}
                  onChange={(e) => setNewOrgData({ ...newOrgData, linkedin_url: e.target.value })}
                  placeholder="https://linkedin.com/company/acme"
                  data-testid="new-org-linkedin"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="org-chro-name">CHRO Name</Label>
                <Input
                  id="org-chro-name"
                  value={newOrgData.chro_name}
                  onChange={(e) => setNewOrgData({ ...newOrgData, chro_name: e.target.value })}
                  placeholder="Jane Smith"
                  data-testid="new-org-chro-name"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="org-chro-email">CHRO Email</Label>
                <Input
                  id="org-chro-email"
                  type="email"
                  value={newOrgData.chro_email}
                  onChange={(e) => setNewOrgData({ ...newOrgData, chro_email: e.target.value })}
                  placeholder="jane@acme.com"
                  data-testid="new-org-chro-email"
                />
              </div>
              
              <div className="space-y-2 col-span-2">
                <Label htmlFor="org-notes">Internal Notes</Label>
                <Input
                  id="org-notes"
                  value={newOrgData.internal_notes}
                  onChange={(e) => setNewOrgData({ ...newOrgData, internal_notes: e.target.value })}
                  placeholder="Notes visible only to admins..."
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowNewOrgModal(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="submit-new-org">
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Organisation'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminOrgList;
